import { useMemo } from "react";
import { useSelector } from "react-redux";
import { RootState } from "store/reducers";
import { DestinationDescriptor } from "views/page-content/platforms/platform/simulcast/types";
import { PlatformId } from "views/page-content/platforms/types";
import CheckmarkIcon from "assets/icons/checkmark-filled.svg?react";
import ErrorIcon from "assets/icons/exclamation.svg?react";
import styles from "../index.module.scss";
import { useTranslation } from "react-i18next";
import { Spinner } from "components/spinners/Spinner";
import { DestinationProcessingStatus } from "..";

export const ProcessingDestination = ({
    destination,
    status,
    error
}: {
    destination: DestinationDescriptor;
    status: DestinationProcessingStatus;
    error?: string;
}) => {
    const { t } = useTranslation();
    const { showInCatalog } = useSelector(
        (state: RootState) => state.platforms.general.generalForm
    );
    const { playlistIds } = useSelector(
        (state: RootState) => state.platforms.videoplayer.videoPlayerForm
    );

    let specificDestinationName = destination.name;
    if (destination.platform === PlatformId.Facebook) {
        const precursor =
            destination.platformSpecificSettings?.edge === "me"
                ? `Timeline (${destination.platformSpecificSettings?.selectedVisibility?.name})`
                : "Page";
        specificDestinationName += ` - ${precursor}`;
    }

    let fullDestinationName = `${t(
        `platforms:${destination.platform}`
    )} - ${specificDestinationName}`;

    if (destination.platform === PlatformId.VideoPlayer) {
        const catalogCopy = t("simulcast:video-catalog");
        const collectionsCopy = t("video-upload:collection-count", {
            count: playlistIds.length,
            plural: playlistIds.length > 1 ? t("video-upload:plural") : ""
        });

        const hasCollections = playlistIds.length > 0;
        if (showInCatalog && hasCollections) {
            fullDestinationName = `${catalogCopy} - ${collectionsCopy}`;
        } else if (showInCatalog) {
            fullDestinationName = catalogCopy;
        } else if (hasCollections) {
            fullDestinationName = collectionsCopy;
        }
    }

    const indicatorIcon = useMemo(() => {
        switch (status) {
            case DestinationProcessingStatus.Processing:
                return <Spinner size={20} alignment="right" />;
            case DestinationProcessingStatus.Success:
                return <CheckmarkIcon className={styles.checkmark} />;
            case DestinationProcessingStatus.Error:
                return <ErrorIcon className={styles.exclamation} />;
            default:
                return null;
        }
    }, [status]);

    return (
        <li className={`${styles["processing-destination"]} ${styles[status]}`}>
            <p>{fullDestinationName}</p>
            {indicatorIcon}
            {error && (
                <p className={styles["destination-specific-error"]}>{error}</p>
            )}
        </li>
    );
};
